import { useCallback, useMemo, useState } from "react";

import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Modal from "../../components/Modal";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import { useAccessSettings } from "./useQueryBoard";
import { useTimeoutFunction } from "./useTimeoutFunction";

import {
  MUTATION_SWITCH_SHELLY_CONTROLLER,
  MUTATION_SWITCH_TUYA_CONTROLLER,
} from "../../config/graphql/mutation";

type AccessSwitchStatus = "error" | "loading" | "success";

export const useAccessControlAutoSwitch = () => {
  const navigate = useNavigate();
  const { activeAccess } = useAccessSettings();

  const [doorStatus, setDoorStatus] = useState<AccessSwitchStatus>();

  const trigger = useCallback(
    async (id: string) => {
      setDoorStatus("loading");

      try {
        await client.request(
          activeAccess === "shelly"
            ? MUTATION_SWITCH_SHELLY_CONTROLLER
            : MUTATION_SWITCH_TUYA_CONTROLLER,
          {
            input: {
              id,
              action: activeAccess === "shelly" ? "ON_WITH_DELAY" : "ON",
            },
          },
        );

        setDoorStatus("success");
      } catch {
        setDoorStatus("error");
      }
    },
    [activeAccess],
  );

  const onDismissDoor = useCallback(() => {
    if (doorStatus === "error") {
      setDoorStatus(undefined);
      return;
    }

    navigate("/", { replace: true });
  }, [doorStatus, navigate]);

  const doorTimeout = useMemo(() => {
    if (doorStatus === "error") {
      return 7000;
    }

    if (doorStatus === "success") {
      return 4000;
    }

    return 0;
  }, [doorStatus]);

  useTimeoutFunction(onDismissDoor, doorTimeout);

  const doorModal = doorStatus ? (
    <Modal
      status={doorStatus}
      title={<Trans i18nKey={`access:status.${doorStatus}.title`} />}
      message={<Trans i18nKey={`access:status.${doorStatus}.message`} />}
      onDismiss={onDismissDoor}
    />
  ) : null;

  return { trigger, doorStatus, doorModal };
};
